import {
	useState,
	useContext,
	RefObject
} from 'react';
import {
	H3,
	H4,
	Label,
	Grid,
	Text,
	Spacer,
	WarnChip,
	Button,
	CargoFormContent,
	ShipperClaimRaisedModal,
	toast,
} from '@components';
import { EditShipment } from '@state';
import { useRecoilValue } from 'recoil';
import { TPagedDialogContext, TRaisedClaim } from '@types';
import { EditShipmentContext } from '../EditShipment.Context';
import styles from '../EditShipment.module.scss';
import { useValidation, useCargoForm } from '@hooks';
import { getDimensionsText } from '@utils';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';

type TProps = {
	pagesRef?: RefObject<HTMLElement>;
	context?: TPagedDialogContext;
	refetch?: () => void;
}

export const Cargo = (props: TProps) => {
	const { context, pagesRef, refetch } = props;
	const ctx = useContext(EditShipmentContext);
	const shipment = useRecoilValue(EditShipment);
	const navigate = useNavigate();

	const [generalError, setGeneralError] = useState<string | null>(null);
	const [raisedClaims, setRaisedClaims] = useState<TRaisedClaim[]>([]);

	/* Only the admin shipment page carries the Issues and Claims tab the claims are read on, and the dialog
	   sits over it either way, so it has to go. */
	const viewClaims = () => {
		setRaisedClaims([]);
		ctx?.closeDialog();
		navigate(`/transport/all-loads/${shipment?.id}?page=iandc`);
	};

	const {
		updateValidationErrors,
		connect,
		clearErrors
	} = useValidation({
		pallet_quantities: z.union([z.string(), z.null()]),
		pallet_spaces: z.union([z.string(), z.null()]),
		total_weight: z.union([z.string(), z.null()]),
		width: z.union([z.string(), z.null()]),
		height: z.union([z.string(), z.null()]),
		length: z.union([z.string(), z.null()]),
		use_user_input_cost: z.boolean(),
		user_manually_cost: z.union([z.string(), z.null()]),
		user_manually_fuel_levy: z.union([z.string(), z.null()]),
		use_global_fuel_levy: z.boolean()
	});

	const dealWithErrors = (errPayload: { errors?: { [key: string]: string[] }; message: string }) => {
		if (errPayload.errors) {
			Object.entries(errPayload.errors).forEach(([key, value]) => {
				updateValidationErrors({
					field: key,
					issues: (value as string[]),
					path: key
				});
			});
		} else {
			setGeneralError(errPayload.message);
		}
	};

	const {
		form,
		updateForm,
		updateQty,
		isFLTorPallets,
		isLoose,
		isOther,
		isItem,
		isAllocated,
		totalPallets,
		totalSpaces,
		totalWeight,
		canSubmit,
		recalculated,
		newShipperPrice,
		newCarrierCost,
		newCarrierManualCost,
		oldShipperPrice,
		oldCarrierCost,
		loadingPriceAdjustment,
		editWindow,
		submitCargo,
	} = useCargoForm(shipment, {
		onFormChange: () => ctx?.setUpdateCargoSubmission?.(true),
		onRecalcSuccess: () => {
			setGeneralError(null);
			if (context && pagesRef && pagesRef.current) {
				setTimeout(() => {
					pagesRef && pagesRef.current && pagesRef.current.scrollTo({
						top: pagesRef.current.scrollHeight,
						behavior: 'smooth'
					});
				}, 0);
			}
		},
		onRecalcError: dealWithErrors,
	});

	const cargoAccess = ctx?.access.pages.cargo;
	// One set of figures cannot describe several freight lines, so each is listed on its own.
	const isMultiCargo = (shipment?.cargos?.length ?? 0) > 1;

	const updateCargo = () => {
		submitCargo({
			onSuccess: (claims) => {
				/* An edit past the shipper's invoice charges the difference on rather than moving the price,
				   so say so outright instead of leaving a plain "updated" toast to imply otherwise. */
				setRaisedClaims(claims);
				if (!claims.length) toast.success('Cargo successfully updated');
				setGeneralError(null);
				clearErrors();
				if (refetch) refetch();
				if (context && pagesRef && pagesRef.current) {
					setTimeout(() => {
						pagesRef && pagesRef.current && pagesRef.current.scrollTo({
							top: 0,
							behavior: 'smooth'
						});
					}, 0);
				}
			},
			onError: dealWithErrors,
		});
	};

	return (
		<>
			<H3>Update Cargo Details</H3>
			<Spacer size={16} />
			<Text>We recommend checking all details on the preview panel to the right are correct before confirming this shipment.</Text>
			<Spacer size={24} />
			<H3>{shipment?.reference}</H3>
			<Spacer size={16} />
			<H4>Original Cargo Details</H4>
			<Spacer size={8} />
			{isMultiCargo ? shipment?.cargos?.map((cargo, idx) => (
				<Text key={idx}>
					{[
						cargo.freight_detail || `Item ${idx + 1}`,
						`Qty ${cargo.qty ?? 0}`,
						`${cargo.total_weight ?? 0}kg`,
						getDimensionsText(cargo, true),
					].filter(Boolean).join(' | ')}
				</Text>
			)) : <Grid columns={2} rowGap={'4px'} columnGap={'40px'} columnTemplate={'100px 1fr'}>
				{isFLTorPallets && <>
					<Label>Pallet QTY</Label>
					<Grid.Item>
						<Text>{totalPallets}</Text>
					</Grid.Item>
					<Label>Pallet Spaces</Label>
					<Grid.Item>
						<Text>{totalSpaces}</Text>
					</Grid.Item>
				</>}
				{isItem && <>
					<Label>Quantity</Label>
					<Grid.Item>
						<Text>{totalPallets}</Text>
					</Grid.Item>
				</>}
				<Label>Total Weight</Label>
				<Grid.Item>
					<Text>{totalWeight}kg</Text>
				</Grid.Item>
				{isOther && <>
					<Label>Dimenssions</Label>
					<Grid.Item>
						<Text>{shipment?.cargos?.[0]?.width ?? 0}cm x {shipment?.cargos?.[0]?.length ?? 0}cm x {shipment?.cargos?.[0]?.height ?? 0}cm </Text>
					</Grid.Item>
				</>}
			</Grid>}
			<Spacer size={32} divider />
			{cargoAccess?.fields.superdockRedirect.visible && <>
				<H4>Are you sure you want to update this Superdock?</H4>
				<Text>If you proceed, you'll be taken to the Superdock page to make your edits. Any changes made could impact multiple trucks, please review carefully before saving.</Text>
				<Spacer size={16} />
				<Button
					text={'Update Superdock'}
					onClick={() => navigate(`/transport/superdock/?page=1&perPage=25&filter[search]=${shipment?.reference}`)}
				/>
			</>}
			{cargoAccess?.fields.invoicedWarning.visible && <>
				<WarnChip text={cargoAccess.fields.invoicedWarning.reason ?? ''} stretch />
				{cargoAccess.fields.cargoForm.visible && <Spacer size={16} />}
			</>}
			{cargoAccess?.fields.multiCargoWarning.visible && <>
				<WarnChip text={cargoAccess.fields.multiCargoWarning.reason ?? ''} stretch />
			</>}
			{cargoAccess?.fields.cargoForm.visible && <>
				<CargoFormContent
					form={form}
					isFLTorPallets={isFLTorPallets}
					isLoose={isLoose}
					isOther={isOther}
					isItem={isItem}
					updateForm={updateForm}
					updateQty={updateQty}
					recalculated={recalculated}
					newShipperPrice={newShipperPrice}
					generalError={generalError}
					connect={connect}
					onFieldFocus={(field) => updateValidationErrors({ field }, true)}
					isAllocated={isAllocated}
					newCarrierCost={newCarrierCost}
					newCarrierManualCost={newCarrierManualCost}
					oldShipperPrice={oldShipperPrice}
					oldCarrierCost={oldCarrierCost}
					editWindow={editWindow}
					disabled={cargoAccess?.fields.cargoForm.disabled ?? false}
				/>
				<Spacer size={16} />
				<div className={styles.btns}>
					<Button
						text={'Update Cargo Details'}
						onClick={updateCargo}
						disabled={(cargoAccess?.fields.updateButton.disabled ?? false) || !canSubmit}
						loading={loadingPriceAdjustment}
					/>
				</div>
			</>}
			<ShipperClaimRaisedModal
				claims={raisedClaims}
				onClose={() => setRaisedClaims([])}
				onViewClaims={ctx?.access.userType === 'admin' ? viewClaims : undefined}
			/>
		</>
	);
};
